// 回應連鎖管理器
// Response Chain Manager - 管理效果的回應和連鎖結算

import type { GameState, CardDefinition } from '../../types';
import type {
  ChainEntry,
  ChainState,
  ResponseOption,
  ResponseWindowState,
  ResponseTriggerType,
  ChainResolutionResult,
} from '../../types/response';
import type { ImmediateAction } from './EffectTriggerSystem';
import { parseSpellTiming, canRespondTo } from './SpellTimingParser';
import { getLogger } from '../../utils/logger';

const log = getLogger('ResponseChainManager');

/**
 * 產生唯一 ID
 */
function generateChainId(): string {
  return `chain_${String(Date.now())}_${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * 回應連鎖管理器
 */
export class ResponseChainManager {
  private chain: ChainEntry[] = [];
  private currentPriority: string = '';
  private passCount: number = 0;
  private isResolving: boolean = false;
  private playerIds: [string, string] = ['', ''];
  private responseWindowOpen: boolean = false;
  private cardDefinitions: Map<string, CardDefinition>;

  // 事件監聽器
  private listeners: {
    onChainStart?: (entry: ChainEntry) => void;
    onChainResponse?: (entry: ChainEntry) => void;
    onChainPass?: (playerId: string) => void;
    onChainResolve?: (entry: ChainEntry) => void;
    onChainEnd?: () => void;
    onResponseWindowOpen?: (state: ResponseWindowState) => void;
    onResponseWindowClose?: () => void;
  } = {};

  constructor(cardDefinitions?: Map<string, CardDefinition>) {
    this.cardDefinitions = cardDefinitions ?? new Map<string, CardDefinition>();
  }

  /**
   * 設定卡牌定義
   */
  setCardDefinitions(definitions: Map<string, CardDefinition>): void {
    this.cardDefinitions = definitions;
  }

  /**
   * 註冊事件監聽器
   */
  on<K extends keyof typeof this.listeners>(
    event: K,
    callback: NonNullable<(typeof this.listeners)[K]>
  ): void {
    this.listeners[event] = callback as (typeof this.listeners)[K];
  }

  /**
   * 取得當前連鎖狀態
   */
  getChainState(): ChainState {
    return {
      entries: [...this.chain],
      currentPriority: this.currentPriority,
      passCount: this.passCount,
      isResolving: this.isResolving,
      responseWindowOpen: this.responseWindowOpen,
    };
  }

  /**
   * 設定當前優先權（用於 controller 同步）
   */
  setCurrentPriority(playerId: string): void {
    this.currentPriority = playerId;
    log.debug(`優先權設定為: ${playerId}`);
  }

  /**
   * 開始一個新的連鎖
   */
  startChain(
    sourceCardId: string,
    sourceCardName: string,
    playerId: string,
    effectText: string,
    actions: ImmediateAction[],
    opponentId: string,
    targets?: string[]
  ): ChainEntry {
    const timing = parseSpellTiming(effectText);
    
    const entry: ChainEntry = {
      id: generateChainId(),
      type: 'spell',
      sourceCardId,
      sourceCardName,
      playerId,
      timing: timing.timing,
      effectText,
      actions,
      targets,
      status: 'pending',
      timestamp: Date.now(),
    };

    this.chain = [entry];
    this.passCount = 0;
    this.isResolving = false;
    this.currentPriority = opponentId;
    this.playerIds = [playerId, opponentId];
    this.responseWindowOpen = true;

    this.listeners.onChainStart?.(entry);

    return entry;
  }

  /**
   * 玩家回應
   */
  respond(
    sourceCardId: string,
    sourceCardName: string,
    playerId: string,
    effectText: string,
    actions: ImmediateAction[],
    targets?: string[]
  ): ChainEntry | null {
    // 驗證是否有優先權
    if (playerId !== this.currentPriority) {
      log.debug(`${playerId} 沒有優先權，當前優先權: ${this.currentPriority}`);
      return null;
    }

    // 驗證不是在結算中
    if (this.isResolving) {
      log.debug('連鎖正在結算中，無法回應');
      return null;
    }

    const timing = parseSpellTiming(effectText);

    const entry: ChainEntry = {
      id: generateChainId(),
      type: 'spell',
      sourceCardId,
      sourceCardName,
      playerId,
      timing: timing.timing,
      effectText,
      actions,
      targets,
      status: 'pending',
      timestamp: Date.now(),
    };

    // 加入連鎖頂端
    this.chain.push(entry);
    this.passCount = 0;

    // 優先權交給對方
    this.currentPriority = this.getOpponentId(playerId);

    this.listeners.onChainResponse?.(entry);

    log.debug(`${sourceCardName} 加入連鎖，連鎖長度: ${String(this.chain.length)}`);

    return entry;
  }

  /**
   * 玩家放棄回應
   */
  pass(playerId: string): boolean {
    if (playerId !== this.currentPriority) {
      log.debug(`${playerId} 沒有優先權，無法放棄`);
      return false;
    }

    if (this.isResolving) {
      log.debug('連鎖正在結算中');
      return false;
    }

    this.passCount++;
    this.listeners.onChainPass?.(playerId);

    log.debug(`${playerId} 放棄回應，連續放棄次數: ${String(this.passCount)}`);

    if (this.passCount >= 2) {
      // 雙方都放棄，開始結算
      return true; // 表示應該開始結算
    } else {
      // 優先權交給對方
      this.currentPriority = this.getOpponentId(playerId);
      return false;
    }
  }

  /**
   * 結算連鎖
   */
  resolveChain(
    state: GameState,
    executeAction: (state: GameState, action: ImmediateAction) => GameState
  ): ChainResolutionResult {
    this.isResolving = true;
    this.responseWindowOpen = false;
    this.listeners.onResponseWindowClose?.();

    const resolvedEntries: ChainEntry[] = [];
    const negatedEntries: ChainEntry[] = [];
    let currentState = state;

    log.debug(`開始結算連鎖，共 ${String(this.chain.length)} 個效果`);

    // 從頂端開始結算 (LIFO)
    while (this.chain.length > 0) {
      const entry = this.chain.pop();
      if (!entry) break;

      if (entry.status === 'negated') {
        log.debug(`${entry.sourceCardName} 已被無效化，跳過`);
        negatedEntries.push(entry);
        continue;
      }

      entry.status = 'resolving';
      log.debug(`結算: ${entry.sourceCardName}`);

      // 檢查是否是無效化效果
      if (this.isNegateEffect(entry.effectText)) {
        // 找到要無效化的目標
        const targetEntryId = entry.targets?.[0];
        if (targetEntryId) {
          const targetEntry = this.chain.find(e => e.id === targetEntryId);
          if (targetEntry) {
            targetEntry.status = 'negated';
            targetEntry.negatedBy = entry.id;
            log.debug(`${entry.sourceCardName} 無效化了 ${targetEntry.sourceCardName}`);
          }
        } else {
          // 無效化連鎖中最近的一個敵方效果
          for (let i = this.chain.length - 1; i >= 0; i--) {
            if (this.chain[i].playerId !== entry.playerId && this.chain[i].status === 'pending') {
              this.chain[i].status = 'negated';
              this.chain[i].negatedBy = entry.id;
              log.debug(`${entry.sourceCardName} 無效化了 ${this.chain[i].sourceCardName}`);
              break;
            }
          }
        }
      }

      // 執行效果
      for (const action of entry.actions) {
        currentState = executeAction(currentState, action);
      }

      entry.status = 'resolved';
      resolvedEntries.push(entry);

      this.listeners.onChainResolve?.(entry);
    }

    this.isResolving = false;
    this.chain = [];
    this.passCount = 0;

    this.listeners.onChainEnd?.();

    log.debug(`連鎖結算完成，成功: ${String(resolvedEntries.length)}，無效: ${String(negatedEntries.length)}`);

    return {
      success: true,
      resolvedEntries,
      negatedEntries,
      finalState: currentState,
    };
  }

  /**
   * 取得可用的回應選項
   */
  getAvailableResponses(
    state: GameState,
    playerId: string,
    triggerType: ResponseTriggerType
  ): ResponseOption[] {
    const options: ResponseOption[] = [];
    const playerKey = playerId === state.player1.id ? 'player1' : 'player2';
    const player = state[playerKey];
    const isMyTurn = state.activePlayerId === playerId;

    // 檢查手牌中的魔法卡
    for (const card of player.hand) {
      const definition = this.cardDefinitions.get(card.definitionId);
      if (!definition || definition.type !== 'spell') continue;

      const timing = parseSpellTiming(definition.effectText);

      // 檢查是否可以回應當前事件
      if (canRespondTo(timing.timing, triggerType, isMyTurn)) {
        options.push({
          type: 'spell',
          cardId: card.id,
          cardCode: definition.code,
          cardName: definition.nameZh || definition.name,
          effectText: definition.effectText || '',
          timing: timing.timing,
          requiresTarget: this.requiresTarget(definition.effectText),
        });
      }
    }

    // 檢查場上單位的回應效果
    const side = playerKey === 'player1' ? 'player1Side' : 'player2Side';
    const battlefield = state.battlefield[side];

    for (const line of [battlefield.frontLine, battlefield.backLine]) {
      for (const slot of line) {
        if (!slot.unit) continue;

        const definition = this.cardDefinitions.get(slot.unit.definitionId);
        if (!definition?.effectText) continue;

        const timing = parseSpellTiming(definition.effectText);

        // 檢查單位是否有回應效果
        if (canRespondTo(timing.timing, triggerType, isMyTurn)) {
          options.push({
            type: 'unit_ability',
            cardId: slot.unit.id,
            cardCode: definition.code,
            cardName: definition.nameZh || definition.name,
            effectText: definition.effectText,
            timing: timing.timing,
            requiresTarget: false,
          });
        }
      }
    }

    // 檢查手牌中可以從手牌觸發的單位（如魔法炸彈客）
    if (triggerType === 'spell_cast') {
      for (const card of player.hand) {
        const definition = this.cardDefinitions.get(card.definitionId);
        if (!definition || definition.type !== 'unit') continue;

        // 檢查是否有「敵方打出魔法卡時」的效果
        if (definition.effectText?.includes('敵方打出魔法卡時')) {
          options.push({
            type: 'hand_trigger',
            cardId: card.id,
            cardCode: definition.code,
            cardName: definition.nameZh || definition.name,
            effectText: definition.effectText,
            timing: 'opponent_spell',
            requiresTarget: false,
          });
        }
      }
    }

    return options;
  }

  /**
   * 開啟回應窗口
   */
  openResponseWindow(
    state: GameState,
    triggerType: ResponseTriggerType,
    triggerPlayerId: string,
    triggerCardId?: string,
    triggerCardName?: string
  ): ResponseWindowState {
    const opponentId = this.getOpponentIdFromState(state, triggerPlayerId);
    const availableResponses = this.getAvailableResponses(state, opponentId, triggerType);

    this.responseWindowOpen = true;
    this.currentPriority = opponentId;

    const windowState: ResponseWindowState = {
      isOpen: true,
      triggerType,
      triggerCardId,
      triggerCardName,
      triggerPlayerId,
      currentPriority: opponentId,
      passCount: 0,
      availableResponses,
    };

    this.listeners.onResponseWindowOpen?.(windowState);

    return windowState;
  }

  /**
   * 關閉回應窗口
   */
  closeResponseWindow(): void {
    this.responseWindowOpen = false;
    this.listeners.onResponseWindowClose?.();
  }

  /**
   * 檢查是否有待處理的連鎖
   */
  hasPendingChain(): boolean {
    return this.chain.length > 0 && !this.isResolving;
  }

  /**
   * 清空連鎖（用於取消或錯誤恢復）
   */
  clearChain(): void {
    this.chain = [];
    this.passCount = 0;
    this.isResolving = false;
    this.responseWindowOpen = false;
    this.currentPriority = '';
  }

  // ==================== 私有方法 ====================

  private getOpponentId(playerId: string): string {
    // 從 startChain 時記錄的玩家 ID 取得對手
    if (playerId === this.playerIds[0]) return this.playerIds[1];
    if (playerId === this.playerIds[1]) return this.playerIds[0];
    // fallback: 如果找不到，回傳空字串
    log.warn(`getOpponentId: 找不到 ${playerId} 的對手`);
    return '';
  }

  private getOpponentIdFromState(state: GameState, playerId: string): string {
    return playerId === state.player1.id ? state.player2.id : state.player1.id;
  }

  private isNegateEffect(effectText: string): boolean {
    return effectText.includes('無效化') || effectText.includes('無效');
  }

  private requiresTarget(effectText: string | null | undefined): boolean {
    if (!effectText) return false;
    // 檢查是否需要選擇目標
    return effectText.includes('1單位') ||
           effectText.includes('1個單位') ||
           effectText.includes('選擇') ||
           effectText.includes('敵方1');
  }
}

/**
 * 建立回應連鎖管理器
 */
export function createResponseChainManager(
  cardDefinitions?: Map<string, CardDefinition>
): ResponseChainManager {
  return new ResponseChainManager(cardDefinitions);
}
